// API Base URL — update this to your PHP backend URL
// Development: http://localhost/modernarch/backend_php
// Production:  https://yourdomain.com/api  (or wherever you upload backend_php)

export const API_BASE = process.env.REACT_APP_API_URL ||
  (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
    ? 'http://localhost/modernarch/backend_php'
    : `${window.location.origin}/api`);

// ─── Image URL Helper ─────────────────────────────────────────────
export function fixImageUrl(url) {
  if (!url) return '';
  if (url.startsWith('https://images.unsplash.com') || url.startsWith('https://images.pexels.com')) {
    return url;
  }
  if (url.includes('/uploads/')) {
    const filename = url.split('/uploads/').pop();
    return `${API_BASE}/uploads/${filename}`;
  }
  return url;
}

// ─── Token Management ───────────────────────────────────────────
export const getToken = () => localStorage.getItem('admin_jwt');
export const setToken = (t) => localStorage.setItem('admin_jwt', t);
export const removeToken = () => localStorage.removeItem('admin_jwt');

// ─── Core Fetch Wrapper ─────────────────────────────────────────
async function request(endpoint, options = {}) {
  const token = getToken();
  const isFormData = options.body instanceof FormData;
  const headers = {
    ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };

  const url = `${API_BASE}/index.php?route=${endpoint}`;

  const res = await fetch(url, {
    ...options,
    headers,
    body: isFormData ? options.body : (options.body ? JSON.stringify(options.body) : undefined),
  });

  const data = await res.json().catch(() => ({}));

  if (!res.ok) {
    throw new Error(data.error || `HTTP ${res.status}`);
  }
  return data;
}

// Append extra query params to route string
function withParams(route, params = {}) {
  const q = new URLSearchParams(
    Object.entries(params).filter(([, v]) => v !== undefined && v !== '' && v !== null)
  ).toString();
  return q ? `${route}&${q}` : route;
}

// ─── Auth ────────────────────────────────────────────────────────
export const authApi = {
  login: (username, password) =>
    request('auth', { method: 'POST', body: { username, password } }),

  verify: () => request('auth', { method: 'GET' }),
};

// ─── Projects ────────────────────────────────────────────────────
export const projectsApi = {
  getAll: (params = {}) =>
    request(withParams('projects', params)),

  getById: (id) =>
    request(`projects&id=${id}`),

  create: (data) =>
    request('projects', { method: 'POST', body: data }),

  update: (id, data) =>
    request(`projects&id=${id}`, { method: 'PUT', body: data }),

  delete: (id) =>
    request(`projects&id=${id}`, { method: 'DELETE' }),
};

// ─── Testimonials ─────────────────────────────────────────────────
export const testimonialsApi = {
  getAll: (adminMode = false) =>
    request(adminMode ? 'testimonials&admin=1' : 'testimonials'),

  getById: (id) =>
    request(`testimonials&id=${id}`),

  create: (data) =>
    request('testimonials', { method: 'POST', body: data }),

  update: (id, data) =>
    request(`testimonials&id=${id}`, { method: 'PUT', body: data }),

  delete: (id) =>
    request(`testimonials&id=${id}`, { method: 'DELETE' }),
};

// ─── Enquiries ────────────────────────────────────────────────────
export const enquiriesApi = {
  getAll: (params = {}) =>
    request(withParams('enquiries', params)),

  getById: (id) =>
    request(`enquiries&id=${id}`),

  // Public: submit enquiry from contact form
  submit: (data) =>
    request('enquiries', { method: 'POST', body: data }),

  updateStatus: (id, status) =>
    request(`enquiries&id=${id}`, { method: 'PATCH', body: { status } }),

  delete: (id) =>
    request(`enquiries&id=${id}`, { method: 'DELETE' }),
};

// ─── CTA Analytics ────────────────────────────────────────────────
export const ctaApi = {
  getAll: () => request('cta'),

  track: (action, page) =>
    request('cta', { method: 'POST', body: { action, page } }),
};

// ─── Content ──────────────────────────────────────────────────────
export const contentApi = {
  getAll: () => request('content'),

  getSection: (key) => request(`content&key=${key}`),

  updateSection: (key, data) =>
    request(`content&key=${key}`, { method: 'PUT', body: data }),
};

// ─── Filter Tags ──────────────────────────────────────────────────
export const filtersApi = {
  getAll: () => request('filters'),

  addTag: (category, tag) =>
    request('filters', { method: 'POST', body: { category, tag } }),

  removeTag: (category, tag) =>
    request(`filters&category=${category}&tag=${encodeURIComponent(tag)}`, { method: 'DELETE' }),
};

// ─── Dashboard ────────────────────────────────────────────────────
export const dashboardApi = {
  getStats: () => request('dashboard'),
};

// ─── Uploads ──────────────────────────────────────────────────────
export const uploadApi = {
  getAll: () => request('upload'),

  upload: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return request('upload', { method: 'POST', body: formData });
  },

  delete: (filename) =>
    request(`upload&filename=${encodeURIComponent(filename)}`, { method: 'DELETE' }),
};
